/**
 * STICKY NOTES (docs/01 §8).
 *
 * Free-floating by default, and optionally PINNED TO A ROOM — which is how a
 * maintenance note becomes a pin on the floor plan rather than another row in
 * another list. Notes with an expiry that has passed simply stop being shown,
 * so the board self-cleans without anybody tidying it.
 *
 * No colour picker. `notes.colour` is a stored CSS colour and the design system
 * forbids hardcoded colours in components, so a note posted from the phone
 * takes the system default and is rendered here from tokens.
 */

import { useState } from 'react'
import {
  SpaceEmpty,
  SpaceStyles,
  formatAgo,
  mutationCtx,
  nameLookup,
  useAction,
  useNow,
  useSpace,
  type RoomOption,
  type SpaceScreenProps,
} from './spaceData.js'
import { mutations } from '../../lib/mutations.js'
import type { Note } from '@office/domain'

export interface NotesProps extends SpaceScreenProps {
  readonly tickMs?: number
}

export function Notes(props: NotesProps) {
  const { db, clock, actorId } = props
  const now = useNow(clock, props.tickMs ?? 60_000)
  const space = useSpace(db)
  const run = useAction()
  const nameOf = nameLookup(space.people)

  const [body, setBody] = useState('')
  const [roomId, setRoomId] = useState('')

  const rooms = roomOptions(space.rooms, space.resources)
  const visible = liveNotes(space.notes, now)
  const roomName = (id: string): string => rooms.find(r => r.id === id)?.name ?? 'a room'

  const post = () => {
    const text = body.trim()
    if (text === '') return
    run(() =>
      mutations.addNote(mutationCtx(props), text, undefined, roomId === '' ? undefined : roomId),
    )
    setBody('')
    setRoomId('')
  }

  return (
    <section className="sp" aria-labelledby="sp-notes-h">
      <SpaceStyles />
      <div className="sp-head">
        <h2 className="sp-title" id="sp-notes-h">
          Notes
        </h2>
        <span className="sp-note">{visible.length} on the board</span>
      </div>

      <form
        className="card stack gap-2"
        aria-label="Post a sticky note"
        onSubmit={e => {
          e.preventDefault()
          post()
        }}
      >
        <label htmlFor="sp-note-body" className="sp-note">
          New note
        </label>
        <textarea
          id="sp-note-body"
          className="sp-textarea"
          value={body}
          placeholder="Something the office should know"
          onChange={e => setBody(e.target.value)}
        />
        {rooms.length > 0 ? (
          <div className="row gap-2">
            <label htmlFor="sp-note-room" className="sp-note grow">
              Pin to a room (optional)
            </label>
            <select
              id="sp-note-room"
              className="sp-select"
              value={roomId}
              onChange={e => setRoomId(e.target.value)}
            >
              <option value="">Not pinned</option>
              {rooms.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <button type="submit" className="btn btn-primary" disabled={body.trim() === ''}>
          Post note
        </button>
      </form>

      {visible.length === 0 ? (
        <SpaceEmpty mark="📌" hint="Notes can be pinned to a room, which turns a maintenance note into a pin on the floor plan.">
          The board is clear.
        </SpaceEmpty>
      ) : (
        <ul className="sp-list stack gap-2" aria-label="Sticky notes">
          {visible.map(note => (
            <li key={note.id} className="sp-sticky">
              <p className="sp-sticky-body">{note.body}</p>
              <div className="row gap-2 sp-sticky-foot">
                <span className="sp-note grow">
                  {nameOf(String(note.authorId))} · {formatAgo(note.createdAt, now)}
                </span>
                {note.pinnedToRoomId === null ? null : (
                  <span className="pill">{roomName(String(note.pinnedToRoomId))}</span>
                )}
                {String(note.authorId) === actorId ? (
                  <button
                    type="button"
                    className="btn sp-quiet-btn"
                    aria-label={`Delete note: ${note.body.slice(0, 40)}`}
                    onClick={() => run(() => mutations.deleteNote(mutationCtx(props), String(note.id)))}
                  >
                    Delete
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** Notes whose expiry has not passed, newest first. */
export function liveNotes(notes: readonly Note[], now: number): Note[] {
  return notes
    .filter(n => n.expiresAt === null || n.expiresAt > now)
    .sort((a, b) => b.createdAt - a.createdAt || (a.id < b.id ? 1 : -1))
}

/** Room labels from the floor plan, falling back to resources of kind `room`. */
export function roomOptions(
  rooms: readonly RoomOption[],
  resources: readonly { id: string; name: string; kind: string }[],
): RoomOption[] {
  if (rooms.length > 0) return [...rooms]
  return resources
    .filter(r => r.kind === 'room')
    .map(r => ({ id: String(r.id), name: r.name }))
}
